import * as t from 'io-ts';

import {makeApiType, makeDefaultValueType} from '../api/apiTypes';

/*
 * Supported methods.
 */

export enum SideFrameMethodsEnum {
  // UI.
  ALERT = 'alert',
  CONFIRM = 'confirm',
  FUZZYLIST = 'fuzzylist',
  PROMPT = 'prompt',
  PROMPT_DATE = 'prompt_date',
  REPORT = 'report',

  // Fetch data.
  FETCH_APP_PATH = 'fetch_app_path',
  FETCH_CHANNELS = 'fetch_channels',
  FETCH_CONVERSATION = 'fetch_conversation',
  FETCH_DRAFT = 'fetch_draft',
  FETCH_INBOXES = 'fetch_inboxes',
  FETCH_NOTES = 'fetch_notes',
  FETCH_TAGS = 'fetch_tags',
  FETCH_TEAM = 'fetch_team',
  FETCH_TEAMMATES = 'fetch_allowed_teammates',
  FETCH_RECIPIENTS = 'fetch_recipients',

  // Conversation actions.
  ADD_TOPIC = 'add_topic',
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  MOVE_TO_INBOX = 'move_to_inbox',
  TAG = 'tag',
  UNTAG = 'untag',
  TOGGLE_TAG = 'toggle_tag',
  TOGGLE_ARCHIVED = 'toggle_archived',
  TOGGLE_TRASHED = 'toggle_trashed',

  // Message actions.
  COMPOSE = 'compose',
  REPLY = 'reply',
  REPLY_ALL = 'reply_all',
  FORWARD = 'forward',
  INSERT_HTML = 'insert_html',
  UPDATE_DRAFT = 'update_draft',

  // Miscellaneous.
  COPY_TO_CLIPBOARD = 'copy_clipboard',
  OPEN_POPUP_URL = 'open_popup_url',
  OPEN_AUTH_POPUP = 'open_auth_popup',
  OPEN_URL = 'open_url',
  SEARCH = 'search',

  // Plugin lifecycle.
  CONNECTED = 'plugin_connected',
  DISCONNECTED = 'plugin_disconnected',
  SET_WIDTH = 'set_width',
}

/*
 * Runtime type: Common.
 */

const sideFrameMessageBase = {
  endpoint: t.string,
};

const sideFrameMessageWithSequenceBase = {
  ...sideFrameMessageBase,
  seq: t.Integer,
};

const sideFrameMessageAlertLikeBase = {
  ...sideFrameMessageWithSequenceBase,
  cancelTitle: makeDefaultValueType(t.union([t.null, t.string]), null),
  okTitle: makeDefaultValueType(t.union([t.null, t.string]), null),
  message: t.string,
  title: t.string,
};

/*
 * Runtime type: UI.
 */

const RSFMAlert = t.type({
  ...sideFrameMessageAlertLikeBase,
  type: t.literal(SideFrameMethodsEnum.ALERT),
});

const RSFMConfirm = t.type({
  ...sideFrameMessageAlertLikeBase,
  type: t.literal(SideFrameMethodsEnum.CONFIRM),
});

const RSFMFuzzyListItem = t.intersection([t.type({title: t.string}), t.UnknownRecord]);

const RSFMFuzzyList = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FUZZYLIST),
  items: t.readonlyArray(RSFMFuzzyListItem),
});

const RSFMPrompt = t.type({
  ...sideFrameMessageAlertLikeBase,
  type: t.literal(SideFrameMethodsEnum.PROMPT),
  val: makeDefaultValueType(t.union([t.null, t.string]), null),
});

const RSFMPromptDate = t.intersection([
  t.type({
    ...sideFrameMessageWithSequenceBase,
    type: t.literal(SideFrameMethodsEnum.PROMPT_DATE),
    position: t.type({
      left: t.number,
      top: t.number,
    }),
  }),
  t.partial({
    val: t.Integer,
    before: t.Integer,
    after: t.Integer,
  }),
]);

const RSFMReport = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.REPORT),
  message: t.string,
  title: t.string,
});

/*
 * Runtime type: Fetch data.
 */

const RSFMFetchAppPath = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_APP_PATH),
});

const RSFMFetchChannels = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_CHANNELS),
});

const RSFMFetchConversation = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_CONVERSATION),
});

const RSFMFetchDraft = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_DRAFT),
});

const RSFMFetchInboxes = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_INBOXES),
});

const RSFMFetchNotes = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_NOTES),
});

const RSFMFetchTags = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_TAGS),
});

const RSFMFetchTeam = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_TEAM),
});

const RSFMFetchTeammates = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_TEAMMATES),
});

const RSFMFetchRecipients = t.type({
  ...sideFrameMessageWithSequenceBase,
  type: t.literal(SideFrameMethodsEnum.FETCH_RECIPIENTS),
});

/*
 * Runtime type: Conversation actions.
 */

const RSFMAddTopic = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.ADD_TOPIC),
  topic: t.intersection([
    t.partial({
      type: t.string,
      name: t.string,
    }),
    t.type({
      ext_link: t.string,
    }),
  ]),
});

const RSFMAssign = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.ASSIGN),
  assignee: t.string,
});

const RSFMUnassign = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.UNASSIGN),
});

const RSFMMoveToInbox = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.MOVE_TO_INBOX),
  inbox_alias: t.string,
});

const RSFMTag = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.TAG),
  tag: t.string,
});

const RSFMUntag = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.UNTAG),
  tag: t.string,
});

const RSFMToggleTag = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.TOGGLE_TAG),
  tag: t.string,
});

const RSFMToggleArchived = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.TOGGLE_ARCHIVED),
});

const RSFMToggleTrashed = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.TOGGLE_TRASHED),
});

/*
 * Runtime type: Message actions.
 */

const RSFMRecipientsBase = {
  to: t.union([t.readonlyArray(t.string), t.null]),
  cc: t.union([t.readonlyArray(t.string), t.null]),
  bcc: t.union([t.readonlyArray(t.string), t.null]),
};

const RSFMComposeBase = {
  ...RSFMRecipientsBase,
  attachment_uids: t.union([t.readonlyArray(t.string), t.null]),
  subject: t.union([t.string, t.null]),
  body: t.union([t.string, t.null]),
};

const RSFMCompose = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.COMPOSE),
  }),
  t.partial({
    ...RSFMComposeBase,
    from: t.union([t.string, t.null]),
  }),
]);

const RSFMReply = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.REPLY),
  }),
  t.partial(RSFMComposeBase),
]);

const RSFMReplyAll = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.REPLY_ALL),
  }),
  t.partial(RSFMComposeBase),
]);

const RSFMForward = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.FORWARD),
  }),
  t.partial(RSFMComposeBase),
]);

const RSFMInsertHTML = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.INSERT_HTML),
  html: t.string,
});

const RSFMUpdateDraft = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.UPDATE_DRAFT),
  }),
  t.partial({
    draft_id: t.string,
    recipients: t.partial(RSFMRecipientsBase),
    attachment_uids: t.union([t.readonlyArray(t.string), t.null]),
  }),
]);

/*
 * Runtime type: Miscellaneous.
 */

const RSFMCopyToClipboard = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.COPY_TO_CLIPBOARD),
  payload: t.string,
});

const RSFMOpenPopupUrl = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.OPEN_POPUP_URL),
    url: t.string,
  }),
  t.partial({
    title: t.string,
  }),
]);

const RSFMOpenAuthPopup = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.OPEN_AUTH_POPUP),
    url: t.string,
  }),
  t.partial({
    title: t.string,
  }),
]);

const RSFMOpenUrl = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.OPEN_URL),
  url: t.string,
});

const RSFMSearch = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.SEARCH),
  query: t.string,
});

/*
 * Runtime type: Plugin status.
 */

const RSFMStatusBase = {
  background: t.boolean,
  debug: t.boolean,
};

const RSFMConnected = t.intersection([
  t.type({type: t.literal(SideFrameMethodsEnum.CONNECTED)}),
  t.partial(RSFMStatusBase),
]);

const RSFMDisconnected = t.intersection([
  t.type({
    ...sideFrameMessageBase,
    type: t.literal(SideFrameMethodsEnum.DISCONNECTED),
  }),
  t.partial(RSFMStatusBase),
]);

const RSFMSetWidth = t.type({
  ...sideFrameMessageBase,
  type: t.literal(SideFrameMethodsEnum.SET_WIDTH),
  width: t.number,
});

/*
 * Runtime type: Union type.
 */

const RSideFrameMessageDirty = t.readonly(
  t.taggedUnion('type', [
    // UI.
    RSFMAlert,
    RSFMConfirm,
    RSFMFuzzyList,
    RSFMPrompt,
    RSFMPromptDate,
    RSFMReport,

    // Fetch data.
    RSFMFetchAppPath,
    RSFMFetchChannels,
    RSFMFetchConversation,
    RSFMFetchDraft,
    RSFMFetchInboxes,
    RSFMFetchNotes,
    RSFMFetchTags,
    RSFMFetchTeam,
    RSFMFetchTeammates,
    RSFMFetchRecipients,

    // Conversation actions.
    RSFMAddTopic,
    RSFMAssign,
    RSFMUnassign,
    RSFMMoveToInbox,
    RSFMTag,
    RSFMUntag,
    RSFMToggleTag,
    RSFMToggleArchived,
    RSFMToggleTrashed,

    // Message actions.
    RSFMCompose,
    RSFMReply,
    RSFMReplyAll,
    RSFMForward,
    RSFMInsertHTML,
    RSFMUpdateDraft,

    // Miscellaneous.
    RSFMCopyToClipboard,
    RSFMOpenPopupUrl,
    RSFMOpenAuthPopup,
    RSFMOpenUrl,
    RSFMSearch,

    // Plugin status.
    RSFMConnected,
    RSFMDisconnected,
    RSFMSetWidth,
  ]),
  'pluginMessage',
);

/*
 * Types.
 */

// UI.
export interface SideFrameMessageAlertModel extends t.TypeOf<typeof RSFMAlert> {}
export interface SideFrameMessageConfirmModel extends t.TypeOf<typeof RSFMConfirm> {}
export interface SideFrameMessageFuzzyListModel extends t.TypeOf<typeof RSFMFuzzyList> {}
export interface SideFrameMessageFuzzyListItem extends t.TypeOf<typeof RSFMFuzzyListItem> {}
export interface SideFrameMessagePromptModel extends t.TypeOf<typeof RSFMPrompt> {}
export interface SideFrameMessagePromptDateModel extends t.TypeOf<typeof RSFMPromptDate> {}
export interface SideFrameMessageReportModel extends t.TypeOf<typeof RSFMReport> {}

// Fetch data.
export interface SideFrameMessageFetchAppPathModel extends t.TypeOf<typeof RSFMFetchAppPath> {}
export interface SideFrameMessageFetchChannelsModel extends t.TypeOf<typeof RSFMFetchChannels> {}
export interface SideFrameMessageFetchConversation extends t.TypeOf<typeof RSFMFetchConversation> {}
export interface SideFrameMessageFetchDraftModel extends t.TypeOf<typeof RSFMFetchDraft> {}
export interface SideFrameMessageFetchInboxesModel extends t.TypeOf<typeof RSFMFetchInboxes> {}
export interface SideFrameMessageFetchNotesModel extends t.TypeOf<typeof RSFMFetchNotes> {}
export interface SideFrameMessageFetchTagsModel extends t.TypeOf<typeof RSFMFetchTags> {}
export interface SideFrameMessageFetchTeamModel extends t.TypeOf<typeof RSFMFetchTeam> {}
export interface SideFrameMessageFetchTeammatesModel extends t.TypeOf<typeof RSFMFetchTeammates> {}
export interface SideFrameMessageFetchRecipientsModel extends t.TypeOf<typeof RSFMFetchRecipients> {}

// Conversation actions.
export interface SideFrameMessageAddTopicModel extends t.TypeOf<typeof RSFMAddTopic> {}
export interface SideFrameMessageAssignModel extends t.TypeOf<typeof RSFMAssign> {}
export interface SideFrameMessageUnassignModel extends t.TypeOf<typeof RSFMUnassign> {}
export interface SideFrameMessageMoveToInboxModel extends t.TypeOf<typeof RSFMMoveToInbox> {}
export interface SideFrameMessageTagModel extends t.TypeOf<typeof RSFMTag> {}
export interface SideFrameMessageUntagModel extends t.TypeOf<typeof RSFMUntag> {}
export interface SideFrameMessageToggleTagModel extends t.TypeOf<typeof RSFMToggleTag> {}
export interface SideFrameMessageToggleArchivedModel extends t.TypeOf<typeof RSFMToggleArchived> {}
export interface SideFrameMessageToggleTrashedModel extends t.TypeOf<typeof RSFMToggleTrashed> {}

// Message actions.
export interface SideFrameMessageComposeModel extends t.TypeOf<typeof RSFMCompose> {}
export interface SideFrameMessageReplyModel extends t.TypeOf<typeof RSFMReply> {}
export interface SideFrameMessageReplyAllModel extends t.TypeOf<typeof RSFMReplyAll> {}
export interface SideFrameMessageForwardModel extends t.TypeOf<typeof RSFMForward> {}
export interface SideFrameMessageInsertHTMLModel extends t.TypeOf<typeof RSFMInsertHTML> {}
export interface SideFrameMessageUpdateDraftModel extends t.TypeOf<typeof RSFMUpdateDraft> {}

// Miscelleanous.
export interface SideFrameMessageCopyToClipboardModel extends t.TypeOf<typeof RSFMCopyToClipboard> {}
export interface SideFrameMessageOpenPopupUrlModel extends t.TypeOf<typeof RSFMOpenPopupUrl> {}
export interface SideFrameMessageOpenAuthPopupModel extends t.TypeOf<typeof RSFMOpenAuthPopup> {}
export interface SideFrameMessageOpenUrlModel extends t.TypeOf<typeof RSFMOpenUrl> {}
export interface SideFrameMessageSearchModel extends t.TypeOf<typeof RSFMSearch> {}

// Plugin status.
export interface SideFrameMessagePluginConnectedModel extends t.TypeOf<typeof RSFMConnected> {}
export interface SideFrameMessagePluginDisconnectedModel extends t.TypeOf<typeof RSFMDisconnected> {}
export interface SideFrameMessageSetWidthModel extends t.TypeOf<typeof RSFMSetWidth> {}

export type SideFrameUIMessageModel =
  | SideFrameMessageAlertModel
  | SideFrameMessageConfirmModel
  | SideFrameMessageFuzzyListModel
  | SideFrameMessagePromptModel
  | SideFrameMessagePromptDateModel
  | SideFrameMessageReportModel;

export type SideFrameMessageWithResultModel =
  | SideFrameUIMessageModel
  | SideFrameMessageFetchAppPathModel
  | SideFrameMessageFetchChannelsModel
  | SideFrameMessageFetchConversation
  | SideFrameMessageFetchDraftModel
  | SideFrameMessageFetchInboxesModel
  | SideFrameMessageFetchNotesModel
  | SideFrameMessageFetchTagsModel
  | SideFrameMessageFetchTeamModel
  | SideFrameMessageFetchTeammatesModel
  | SideFrameMessageFetchRecipientsModel;

export type SideFrameMessageModel =
  // UI + Fetch data.
  | SideFrameMessageWithResultModel
  // Conversation actions.
  | SideFrameMessageAddTopicModel
  | SideFrameMessageAssignModel
  | SideFrameMessageUnassignModel
  | SideFrameMessageMoveToInboxModel
  | SideFrameMessageTagModel
  | SideFrameMessageUntagModel
  | SideFrameMessageToggleTagModel
  | SideFrameMessageToggleArchivedModel
  | SideFrameMessageToggleTrashedModel
  // Message actions.
  | SideFrameMessageComposeModel
  | SideFrameMessageReplyModel
  | SideFrameMessageReplyAllModel
  | SideFrameMessageForwardModel
  | SideFrameMessageInsertHTMLModel
  | SideFrameMessageUpdateDraftModel
  // Miscellaneous.
  | SideFrameMessageCopyToClipboardModel
  | SideFrameMessageOpenPopupUrlModel
  | SideFrameMessageOpenAuthPopupModel
  | SideFrameMessageOpenUrlModel
  | SideFrameMessageSearchModel
  // Plugin status.
  | SideFrameMessagePluginConnectedModel
  | SideFrameMessagePluginDisconnectedModel
  | SideFrameMessageSetWidthModel;

const RSideFrameMessage = t.clean<SideFrameMessageModel>(RSideFrameMessageDirty as any);

/*
 * Type guard.
 */

export function isSideFrameMessageWithResult(
  pluginMessage: SideFrameMessageModel,
): pluginMessage is SideFrameMessageWithResultModel {
  return Boolean((pluginMessage as any).seq);
}

/*
 * IO methods.
 */

function parseSideFrameMessage(src: any): SideFrameMessageModel {
  return src;
}

/*
 * API type.
 */

export const ApiSideFrameMessage = makeApiType('sideFrameMessage', RSideFrameMessage, parseSideFrameMessage);
